import { Link } from 'react-router-dom';

const sections = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    content: [
      {
        subtitle: 'Information You Provide Directly',
        text: 'When you fill out any form on our website — including enrollment requests, contact inquiries, booking consultations, or leaving a review — we may collect your name, email address, phone number, and any additional details you choose to share.',
      },
      {
        subtitle: 'Automatically Collected Information',
        text: 'When you visit our website, certain information may be collected automatically, including your browser type, device type, pages visited, and general geographic location. This information helps us understand how visitors use our site.',
      },
    ],
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Information',
    content: [
      {
        subtitle: '',
        text: 'We use the information we collect for the following purposes:',
        list: [
          'To respond to your inquiries and enrollment requests',
          'To schedule and coordinate consultations or sessions',
          'To send you information about our programs, services, and updates (only if you have opted in)',
          'To improve our website and the experience we provide',
          'To comply with legal obligations',
        ],
      },
    ],
  },
  {
    id: 'sharing',
    title: 'Sharing Your Information',
    content: [
      {
        subtitle: '',
        text: 'Creative Institute LLC does not sell, trade, or rent your personal information to third parties. We may share information only in the following limited circumstances:',
        list: [
          'With trusted service providers who assist us in operating our website and conducting our business, subject to confidentiality agreements',
          'When required by law or to protect the rights, property, or safety of Creative Institute LLC, our students, or others',
          'With your explicit consent',
        ],
      },
    ],
  },
  {
    id: 'children',
    title: "Children's Privacy",
    content: [
      {
        subtitle: '',
        text: "Creative Institute LLC serves students of all ages, including minors. We do not knowingly collect personal information directly from children under the age of 13 without verifiable parental consent. All enrollment and contact forms are intended to be completed by parents, guardians, or adults. If you believe we have inadvertently collected information from a child under 13 without proper consent, please contact us immediately and we will take steps to remove that information.",
      },
    ],
  },
  {
    id: 'data-security',
    title: 'Data Security',
    content: [
      {
        subtitle: '',
        text: 'We take the security of your personal information seriously. We implement reasonable administrative, technical, and physical safeguards to protect your information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
      },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking',
    content: [
      {
        subtitle: '',
        text: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyze site traffic. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. If you do not accept cookies, some portions of our website may not function properly.',
      },
    ],
  },
  {
    id: 'third-party',
    title: 'Third-Party Services',
    content: [
      {
        subtitle: '',
        text: 'Our website uses third-party services to support its functionality, including form processing and website hosting. These services have their own privacy policies governing how they handle your data. We encourage you to review the privacy policies of any third-party services you interact with through our site.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    content: [
      {
        subtitle: '',
        text: 'You have the right to:',
        list: [
          'Request access to the personal information we hold about you',
          'Request correction of inaccurate or incomplete information',
          'Request deletion of your personal information, subject to certain exceptions',
          'Opt out of any marketing communications at any time',
          'File a complaint with a relevant data protection authority',
        ],
      },
      {
        subtitle: '',
        text: 'To exercise any of these rights, please contact us using the information provided below.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    content: [
      {
        subtitle: '',
        text: 'We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make material changes, we will update the "Last Updated" date at the top of this page. We encourage you to review this policy periodically.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    content: [
      {
        subtitle: '',
        text: 'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please reach out to us:',
        contact: true,
      },
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="relative z-10 max-w-screen-xl mx-auto px-8 pt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white font-body text-sm mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back to Home
          </Link>
          <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-5">
            Legal
          </span>
          <h1 className="font-heading text-white text-5xl lg:text-6xl font-black mb-4 leading-tight">
            Privacy Policy
          </h1>
          <p className="font-body text-white/60 text-base max-w-xl leading-relaxed">
            Creative Institute LLC is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information.
          </p>
          <p className="font-body text-white/40 text-sm mt-5">
            Last Updated: March 31, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-14 items-start">

            {/* Sticky sidebar nav */}
            <aside className="hidden lg:block flex-shrink-0 w-64 sticky top-28">
              <p className="font-body text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">On This Page</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="font-body text-sm text-gray-500 hover:text-brand-blue-dark transition-colors py-1.5 px-3 rounded-lg hover:bg-[#EEF2FF] cursor-pointer leading-snug"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-5 rounded-2xl bg-[#EEF2FF] border border-brand-purple/10">
                <p className="font-body text-brand-blue-dark text-xs font-semibold mb-2">Questions?</p>
                <p className="font-body text-gray-500 text-xs leading-relaxed mb-3">
                  We&apos;re happy to explain anything in plain language.
                </p>
                <Link
                  to="/contact"
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-semibold text-xs cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <i className="ri-mail-line text-xs" /> Contact Us
                </Link>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 max-w-3xl">
              {/* Intro */}
              <div className="p-6 rounded-2xl bg-[#F8F7FF] border border-brand-purple/10 mb-10">
                <p className="font-body text-gray-600 text-sm leading-relaxed">
                  This Privacy Policy applies to the website operated by <strong className="text-brand-blue-dark">Creative Institute LLC</strong>, based in Fayetteville, NC. By using our website, you agree to the collection and use of information as described in this policy.
                </p>
              </div>

              {/* Sections */}
              <div className="flex flex-col gap-12">
                {sections.map((section, idx) => (
                  <div key={section.id} id={section.id} className="scroll-mt-28">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white font-heading text-xs font-bold">
                        {idx + 1}
                      </span>
                      <h2 className="font-heading text-brand-blue-dark text-2xl font-black">
                        {section.title}
                      </h2>
                    </div>

                    <div className="flex flex-col gap-5 pl-10">
                      {section.content.map((block, bIdx) => (
                        <div key={bIdx}>
                          {block.subtitle && (
                            <h3 className="font-body text-brand-blue-dark text-sm font-semibold mb-2">
                              {block.subtitle}
                            </h3>
                          )}
                          {block.text && (
                            <p className="font-body text-gray-600 text-sm leading-relaxed mb-3">
                              {block.text}
                            </p>
                          )}
                          {'list' in block && block.list && (
                            <ul className="flex flex-col gap-2 mt-2">
                              {block.list.map((item: string) => (
                                <li key={item} className="flex items-start gap-3">
                                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0 mt-0.5">
                                    <i className="ri-check-line text-xs" />
                                  </span>
                                  <span className="font-body text-gray-600 text-sm leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {'contact' in block && block.contact && (
                            <div className="mt-3 flex flex-col gap-3">
                              {[
                                { icon: 'ri-mail-line', label: 'Email', value: 'info@creativeinstitutellc.com', href: 'mailto:info@creativeinstitutellc.com' },
                                { icon: 'ri-phone-line', label: 'Phone', value: '910-242-4567', href: 'tel:9102424567' },
                              ].map((item) => (
                                <div key={item.label} className="flex items-center gap-3">
                                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-brand-yellow flex-shrink-0">
                                    <i className={`${item.icon} text-sm`} />
                                  </div>
                                  <div>
                                    <p className="font-body text-gray-400 text-xs uppercase tracking-widest">{item.label}</p>
                                    {item.href ? (
                                      <a href={item.href} className="font-body text-brand-blue-dark text-sm font-semibold hover:text-brand-purple transition-colors cursor-pointer">
                                        {item.value}
                                      </a>
                                    ) : (
                                      <p className="font-body text-brand-blue-dark text-sm font-semibold">{item.value}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {idx < sections.length - 1 && (
                      <div className="mt-12 border-b border-gray-100" />
                    )}
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <div className="mt-14 p-6 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white">
                <p className="font-heading text-white font-bold text-base mb-2">
                  Where Every Creative Mind Matters
                </p>
                <p className="font-body text-white/70 text-sm leading-relaxed">
                  Your trust means everything to us. If you ever have concerns about how we handle your information, please don&apos;t hesitate to reach out — we&apos;re always happy to talk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
